import Cookies from 'js-cookie';

/**
 * Type for pk-admin cookie data
 */
export interface PkAdminCookie {
    token?: string;
    tokenExpiry?: number;
    username?: string;
    userID?: number;
    organisationID?: number;
    email?: string;
    backoffice_organisation?: boolean;
    backoffice_organisation_id?: number;
    belongs_to_backoffice?: number | null;
    permissions?: Record<string, number>;
    isAdmin?: boolean;
    organisation_logo?: string;
    type?: string;
    allow_finance?: boolean;
    allow_sub_rules?: boolean;
    allow_ticket_rules?: boolean;
    language?: string;
    authMethod?: 'email_password' | 'entra';
}

/**
 * Type for pk-base-admin cookie data
 */
export interface PkBaseAdminCookie {
    username?: string;
    [key: string]: unknown;
}

/**
 * Get cookie value and parse as JSON.
 * Replacement for Cookies.getJSON which was removed in js-cookie v3.
 */
export function getCookieJSON<T = Record<string, unknown>>(
    name: string
): T | undefined {
    const value = Cookies.get(name);
    if (!value) {
        return undefined;
    }
    try {
        return JSON.parse(value) as T;
    } catch {
        return undefined;
    }
}

// ──────────────────────────────────────────────────────────────────
// The takeover backup (original admin's session while impersonating
// another user) lives in localStorage instead of a cookie. This keeps
// the HTTP Cookie header small during takeover — combined cookie size
// was previously ~6 KB (pk-admin + pk-base-admin), now ~3 KB.
// Login / refresh / logout flows are unchanged; permissions still live
// in pk-admin like before.
// ──────────────────────────────────────────────────────────────────

const BASE_ADMIN_STORAGE_KEY = 'pk-base-admin';
const BASE_ADMIN_COOKIE_FALLBACK = 'pk-base-admin-fallback';

// localStorage may be unavailable (Safari private mode, full quota,
// enterprise policy). If it is, fall back to a cookie so takeover still
// works — at the cost of bringing back the larger Cookie header for
// that user. The vast majority of users go through localStorage.

export const getBaseAdmin = (): PkBaseAdminCookie | undefined => {
    let raw: string | null = null;
    try {
        raw = localStorage.getItem(BASE_ADMIN_STORAGE_KEY);
    } catch {
        // localStorage unavailable — fall through to cookie fallback
    }
    if (raw) {
        try {
            return JSON.parse(raw) as PkBaseAdminCookie;
        } catch {
            // Stored value is corrupt — wipe so we don't trip on it
            // every call from here on.
            try {
                localStorage.removeItem(BASE_ADMIN_STORAGE_KEY);
            } catch {
                // ignore
            }
        }
    }
    return getCookieJSON<PkBaseAdminCookie>(BASE_ADMIN_COOKIE_FALLBACK);
};

export const setBaseAdmin = (data: PkBaseAdminCookie): void => {
    // Remove any legacy cookie so it can't keep contributing to the
    // Cookie header even when localStorage succeeds.
    Cookies.remove('pk-base-admin');
    try {
        localStorage.setItem(BASE_ADMIN_STORAGE_KEY, JSON.stringify(data));
        Cookies.remove(BASE_ADMIN_COOKIE_FALLBACK);
        return;
    } catch {
        // fall through to cookie fallback
    }
    try {
        Cookies.set(BASE_ADMIN_COOKIE_FALLBACK, JSON.stringify(data));
    } catch {
        // last resort — silently fail.
    }
};

export const clearBaseAdmin = (): void => {
    try {
        localStorage.removeItem(BASE_ADMIN_STORAGE_KEY);
    } catch {
        // ignore
    }
    Cookies.remove(BASE_ADMIN_COOKIE_FALLBACK);
    Cookies.remove('pk-base-admin'); // legacy cookie cleanup
};
